import { Plus } from 'lucide-react'
import { useMemo } from 'react'
import { Button, CustomerList, CustomerForm, CustomerFilters } from '../components'
import { useCustomers } from '../hooks/useCustomers'
import { useAuth } from '../contexts/AuthContextTest'

function Customers() {
  const { user } = useAuth()
  
  const {
    customers,
    isLoading,
    totalItems,
    showModal,
    editingCustomer,
    isCreating,
    isUpdating,
    searchTerm,
    setSearchTerm,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handleClearFilters,
    refreshCustomers
  } = useCustomers({}, user)

  // Calculate measurements count from customer data (if measurements are included in customer object)
  const measurementsCount = useMemo(() => {
    if (!customers || customers.length === 0) return {}
    
    const countMap = {}
    customers.forEach(customer => {
      // Check if customer has measurements array or measurementsCount field
      if (customer.measurements && Array.isArray(customer.measurements)) {
        countMap[customer.id] = customer.measurements.length
      } else if (customer.measurementsCount !== undefined) {
        countMap[customer.id] = customer.measurementsCount
      } else {
        countMap[customer.id] = 0
      }
    })
    
    return countMap
  }, [customers])

  // Refresh measurements count (will be called after measurements are modified)
  const handleMeasurementsChange = () => {
    // If measurements are included in customer data, refresh customers to get updated measurements
    if (refreshCustomers) {
      refreshCustomers()
    }
  }

  // Check if user can create customers
  const canCreateCustomer = user?.role?.id === 1 || user?.role.id === 2 // Only Super Admin can create customers

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Customers</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage customer information and relationships
          </p>
        </div>
        {canCreateCustomer && (
          <Button
            onClick={() => handleEdit(null)}
            variant="primary"
            size="md"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        )}
      </div>

      {/* Filters */}
      <CustomerFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onClearFilters={handleClearFilters}
        totalItems={totalItems}
      />

      {/* Customer List */}
      <CustomerList
        customers={customers}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        userRole={user?.role?.id}
        measurementsCount={measurementsCount}
        onMeasurementsChange={handleMeasurementsChange}
      />

      {/* Customer Form Modal */}
      <CustomerForm
        isOpen={showModal}
        editingCustomer={editingCustomer}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
      />
    </div>
  )
}

export default Customers